import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Button,
  Typography,
  Container,
  CssBaseline,
  Input,
  CircularProgress,
} from "@material-ui/core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPencilAlt } from "@fortawesome/free-solid-svg-icons";
import { withStyles } from "@material-ui/core/styles";
import loginBg from "../../assets/images/pc_loginBg.jpg";
import cmsAdminLogo from "../../assets/images/cmsAdminLogo.png";
import axiosInstance from "../../services/axios";
import { BaseUrl } from "../../services/config";
import useForceLightMode from "../../hooks/useForceLightMode";
import { useToast } from "../../hooks/useToast";

const styles = (theme) => ({
  pageroot: {
    background: "url(" + loginBg + ") top center no-repeat",
    backgroundSize: "cover",
    backgroundAttachment: "fixed",
    height: "100vh",
    paddingTop: theme.spacing(2),
  },
});

const CompleteRegistration = (props) => {
  useForceLightMode();
  const navigate = useNavigate();
  const { toast } = useToast();
  const params = new URLSearchParams(window.location.search);
  const tokenFromUrl = params.get("token") || "";

  const [token, setToken] = useState(tokenFromUrl);
  const [emailHint, setEmailHint] = useState("");
  const [status, setStatus] = useState("loading");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [renewMessage, setRenewMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateToken = useCallback(async (t) => {
    if (!t) {
      setStatus("invalid");
      return;
    }
    setStatus("loading");
    try {
      const res = await axiosInstance.post(
        `${BaseUrl}validateCompleteRegistrationToken`,
        { token: t },
        { skipAuthRedirect: true }
      );
      if (res.data?.status === true) {
        setEmailHint(res.data.email || "");
        setStatus("ready");
      } else {
        setStatus("invalid");
      }
    } catch (err) {
      if (err.response?.status === 410 && err.response?.data?.code === "expired") {
        try {
          const renew = await axiosInstance.post(
            `${BaseUrl}renewCompleteRegistrationToken`,
            { token: t },
            { skipAuthRedirect: true }
          );
          if (renew.data?.status === true && renew.data?.token) {
            setRenewMessage(renew.data.message || "");
            const next = renew.data.token;
            setToken(next);
            navigate(`/complete-registration?token=${encodeURIComponent(next)}`, {
              replace: true,
            });
            setEmailHint("");
            setStatus("loading");
            const v2 = await axiosInstance.post(
              `${BaseUrl}validateCompleteRegistrationToken`,
              { token: next },
              { skipAuthRedirect: true }
            );
            if (v2.data?.status === true) {
              setEmailHint(v2.data.email || "");
              setStatus("ready");
            } else {
              setStatus("invalid");
            }
            return;
          }
        } catch (renewErr) {
          console.error(renewErr);
        }
        setStatus("expired");
      } else {
        setStatus("invalid");
      }
    }
  }, [navigate]);

  useEffect(() => {
    validateToken(tokenFromUrl);
  }, [tokenFromUrl, validateToken]);

  const validatePassword = (value) => {
    if (value.length < 8) {
      const m = "Password must be at least 8 characters long";
      setErrorMessage(m);
      return { ok: false, message: m };
    }
    if (!/\d/.test(value)) {
      const m = "Password must contain at least one number";
      setErrorMessage(m);
      return { ok: false, message: m };
    }
    if (!/[a-z]/.test(value)) {
      const m = "Password must contain at least one lowercase letter";
      setErrorMessage(m);
      return { ok: false, message: m };
    }
    if (!/[A-Z]/.test(value)) {
      const m = "Password must contain at least one uppercase letter";
      setErrorMessage(m);
      return { ok: false, message: m };
    }
    setErrorMessage("");
    return { ok: true, message: "" };
  };

  const handleInputChange = (e) => {
    setPassword(e.target.value);
    validatePassword(e.target.value);
  };

  const handlePaste = (e) => {
    e.preventDefault();
  };

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    if (password !== confirmPassword) {
      setErrorMessage("Passwords do not match");
      return;
    }
    const passCheck = validatePassword(password);
    if (!passCheck.ok) return;

    setIsSubmitting(true);
    try {
      await axiosInstance.post(
        `${BaseUrl}completeRegistrationWithToken`,
        { token, password },
        { skipAuthRedirect: true }
      );
      toast?.({
        title: "Registration complete",
        description: "Your password was saved. Redirecting to sign in…",
        variant: "default",
      });
      setTimeout(() => navigate("/login"), 1500);
    } catch (err) {
      if (err.response?.status === 410) {
        try {
          const renew = await axiosInstance.post(
            `${BaseUrl}renewCompleteRegistrationToken`,
            { token },
            { skipAuthRedirect: true }
          );
          if (renew.data?.token) {
            const msg =
              renew.data.message ||
              "This link expired. We sent a new link to your email and phone.";
            setRenewMessage(msg);
            setToken(renew.data.token);
            navigate(`/complete-registration?token=${encodeURIComponent(renew.data.token)}`, {
              replace: true,
            });
            await validateToken(renew.data.token);
            toast?.({
              title: "Link refreshed",
              description: msg,
              variant: "default",
            });
            return;
          }
        } catch (renewErr) {
          console.error(renewErr);
        }
      }
      const msg =
        err.response?.data?.message ||
        err.message ||
        "Could not complete registration.";
      toast?.({
        title: "Could not complete registration",
        description: msg,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const { classes } = props;

  if (status === "loading") {
    return (
      <div className={classes.pageroot}>
        <Container component="main" maxWidth="xs">
          <CssBaseline />
          <div
            style={{
              minHeight: "100vh",
              background: "#f0fdf4",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              padding: "20px",
            }}
          >
            <img src={cmsAdminLogo} alt="ProTeams" style={{ height: 80, marginBottom: 24 }} />
            <div
              style={{
                background: "#fff",
                borderRadius: 10,
                padding: 40,
                boxShadow: "0 4px 15px rgba(0, 128, 0, 0.2)",
                textAlign: "center",
                maxWidth: 400,
                width: "100%",
              }}
            >
              <CircularProgress style={{ color: "#27ae60", marginBottom: 20 }} />
              <Typography style={{ color: "#065f46", fontWeight: 600, fontSize: 18, marginBottom: 8 }}>
                Verifying your link
              </Typography>
              <Typography style={{ fontSize: 15, color: "#555", lineHeight: 1.5 }}>
                {renewMessage || "Please wait while we confirm your invitation…"}
              </Typography>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  if (status === "invalid" || status === "expired") {
    return (
      <div className={classes.pageroot}>
        <Container component="main" maxWidth="xs">
          <CssBaseline />
          <div
            style={{
              minHeight: "100vh",
              background: "#f0fdf4",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              padding: "20px",
            }}
          >
            <img src={cmsAdminLogo} alt="ProTeams" style={{ height: 80, marginBottom: 24 }} />
            <div
              style={{
                background: "#fff",
                borderRadius: 10,
                padding: 32,
                boxShadow: "0 4px 15px rgba(0, 128, 0, 0.2)",
                textAlign: "center",
                maxWidth: 420,
              }}
            >
              <Typography style={{ color: "#065f46", fontWeight: 600, marginBottom: 16 }}>
                {status === "expired" ? "This registration link expired" : "Invalid registration link"}
              </Typography>
              <Typography style={{ fontSize: 15, color: "#444", marginBottom: 20 }}>
                {status === "expired"
                  ? "Open the same invitation link again from your email or text, or ask your administrator to resend your invite."
                  : "Ask your administrator for a new invitation."}
              </Typography>
              <Button
                variant="contained"
                onClick={() => navigate("/login")}
                style={{ backgroundColor: "#27ae60", color: "#fff", textTransform: "none" }}
              >
                Back to sign in
              </Button>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className={classes.pageroot}>
      <Container component="main" maxWidth="xs">
        <CssBaseline />
        <div
          style={{
            minHeight: "100vh",
            background: "#f0fdf4",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            padding: "20px",
          }}
        >
          <img src={cmsAdminLogo} alt="ProTeams" style={{ height: 80, marginBottom: 30 }} />
          <div
            style={{
              background: "#fff",
              borderRadius: 10,
              padding: 40,
              boxShadow: "0 4px 15px rgba(0, 128, 0, 0.2)",
              textAlign: "center",
              maxWidth: 400,
              width: "100%",
            }}
          >
            <form noValidate onSubmit={handleSubmit}>
              <Typography
                variant="h5"
                style={{
                  color: "#065f46",
                  fontWeight: 600,
                  marginBottom: 12,
                  fontSize: 20,
                }}
              >
                Set your password
              </Typography>
              {emailHint ? (
                <Typography
                  variant="body2"
                  style={{ marginBottom: 20, color: "#555", lineHeight: 1.5 }}
                >
                  Create the password you will use to sign in to ProTeams for{" "}
                  <span style={{ fontWeight: 700, color: "#374151" }}>{emailHint}</span>.
                </Typography>
              ) : null}
              {renewMessage ? (
                <Typography
                  variant="body2"
                  style={{
                    marginBottom: 20,
                    padding: "12px 14px",
                    background: "#ecfdf5",
                    borderRadius: 8,
                    color: "#065f46",
                    textAlign: "left",
                    lineHeight: 1.5,
                    border: "1px solid #a7f3d0",
                  }}
                >
                  {renewMessage}
                </Typography>
              ) : null}

              <div className="formFieldGroup" style={{ marginBottom: 20, textAlign: "left" }}>
                <Input
                  fullWidth
                  disableUnderline
                  disabled={isSubmitting}
                  onChange={handleInputChange}
                  value={password}
                  className="input"
                  name="password"
                  id="complete-reg-password"
                  placeholder="Enter New Password"
                  type={showPassword ? "text" : "password"}
                  startAdornment={
                    <span className="input-adornment">
                      <FontAwesomeIcon
                        icon={faPencilAlt}
                        onClick={togglePasswordVisibility}
                        style={{ cursor: isSubmitting ? "default" : "pointer" }}
                      />
                    </span>
                  }
                  style={{
                    border: "1px solid #d1d5db",
                    borderRadius: 8,
                    padding: "10px 12px",
                    marginBottom: 15,
                  }}
                />
                <Input
                  fullWidth
                  disableUnderline
                  disabled={isSubmitting}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                  value={confirmPassword}
                  className="input"
                  name="confirmPassword"
                  id="complete-reg-confirm-password"
                  placeholder="Confirm Password"
                  type={showPassword ? "text" : "password"}
                  onPaste={handlePaste}
                  startAdornment={
                    <span className="input-adornment">
                      <FontAwesomeIcon
                        icon={faPencilAlt}
                        onClick={togglePasswordVisibility}
                        style={{ cursor: isSubmitting ? "default" : "pointer" }}
                      />
                    </span>
                  }
                  style={{
                    border: "1px solid #d1d5db",
                    borderRadius: 8,
                    padding: "10px 12px",
                  }}
                />
              </div>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={isSubmitting}
                style={{
                  backgroundColor: "#27ae60",
                  color: "#fff",
                  textTransform: "none",
                  fontWeight: 500,
                  padding: "10px 28px",
                  borderRadius: 8,
                  boxShadow: "0 4px 10px rgba(39,174,96,0.3)",
                  marginBottom: 15,
                }}
              >
                {isSubmitting ? (
                  <CircularProgress size={22} style={{ color: "#fff" }} />
                ) : (
                  "SAVE PASSWORD & CONTINUE"
                )}
              </Button>

              {errorMessage ? (
                <Typography color="error" variant="body2" style={{ marginBottom: 8 }}>
                  {errorMessage}
                </Typography>
              ) : null}

              <Typography variant="body2" style={{ marginTop: 15, fontSize: 14 }}>
                Already registered?{" "}
                <Link
                  to="/login"
                  style={{ color: "#27ae60", fontWeight: 500, textDecoration: "none" }}
                >
                  Sign in
                </Link>
              </Typography>
            </form>
          </div>
        </div>
      </Container>
    </div>
  );
};

export default withStyles(styles, { withTheme: true })(CompleteRegistration);
